import type { JSX, ReactNode } from "react";

import { Fieldset } from "@frontend/components/atoms/Fieldset";
import { Input } from "@frontend/components/atoms/Input";
import { useLingui } from "@lingui/react/macro";

import { useAddressAutofill } from "../hooks/useAddressAutofill";
import type { AddressFieldGroup } from "../hooks/useAddressAutofill";
import { useAddressText } from "../hooks/useAddressText";
import type { AddressTextField, ControlSize } from "../types";
import { CityField } from "./CityField";
import { CountryField } from "./CountryField";
import { ExtendedAddressField } from "./ExtendedAddressField";
import { FieldFrame } from "./FieldFrame";
import { PostalCodeField } from "./PostalCodeField";
import { StateField } from "./StateField";
import { StreetField } from "./StreetField";

export type AddressFieldsGroup = AddressFieldGroup & {
  extendedAddress?: AddressTextField;
  postOfficeBox?: AddressTextField;
};

export type AddressFieldsProps = {
  fields: AddressFieldsGroup;
  size?: ControlSize;
  disabled?: boolean;
  autoPostalCode?: boolean;
  autoPostalCodeToggle?: ReactNode;
  place?: ReactNode;
};

export function AddressFields({
  fields,
  size,
  disabled,
  autoPostalCode = true,
  autoPostalCodeToggle,
  place,
}: AddressFieldsProps): JSX.Element {
  const { t } = useLingui();

  const autofill = useAddressAutofill(fields, { postalCode: autoPostalCode });

  return (
    <Fieldset orientation="horizontal" variant="card" size={size ?? "md"}>
      {place}

      <StreetField
        field={fields.street}
        countryField={fields.country}
        label={t`Street`}
        size={size}
        disabled={disabled}
        onAddressSelected={autofill.onAddressSelected}
      />

      {fields.extendedAddress ? (
        <ExtendedAddressField
          field={fields.extendedAddress}
          label={t`Extended address`}
          size={size}
          disabled={disabled}
        />
      ) : null}

      {fields.postOfficeBox ? (
        <PlainField
          field={fields.postOfficeBox}
          label={t`Post office box`}
          size={size}
          disabled={disabled}
        />
      ) : null}

      {fields.postalCode ? (
        <PostalCodeField
          field={fields.postalCode}
          label={t`Postal code`}
          size={size}
          disabled={disabled || autoPostalCode}
        />
      ) : null}

      {autoPostalCodeToggle}

      {fields.city ? (
        <CityField
          field={fields.city}
          countryField={fields.country}
          label={t`City`}
          size={size}
          disabled={disabled}
          onCitySelected={autofill.onCitySelected}
          onCityCleared={autofill.onCityCleared}
        />
      ) : null}

      {fields.state ? (
        <StateField
          field={fields.state}
          countryField={fields.country}
          label={t`Region`}
          size={size}
          disabled={disabled}
        />
      ) : null}

      {fields.country ? (
        <CountryField
          field={fields.country}
          label={t`Country`}
          size={size}
          disabled={disabled}
        />
      ) : null}
    </Fieldset>
  );
}

type PlainFieldProps = {
  field: AddressTextField;
  label: string;
  size?: ControlSize;
  disabled?: boolean;
};

function PlainField({
  field,
  label,
  size,
  disabled,
}: PlainFieldProps): JSX.Element {
  const { value, setValue, error } = useAddressText(field);

  return (
    <FieldFrame label={label} error={error}>
      <Input
        value={value}
        placeholder={label}
        size={size}
        disabled={disabled}
        invalid={Boolean(error)}
        onValueChange={setValue}
      />
    </FieldFrame>
  );
}
